/// <amd-module name="Repos/Views/PullRequest/PullRequestDataService" />
import {
    CommentThreadContext,
    GitPullRequest,
    GitPullRequestCommentThread,
    GitPullRequestCompletionOptions,
    GitPullRequestIteration,
    GitPullRequestStatus,
    GitRef
} from "Repos/Common/Contracts/Git";
import { PolicyEvaluationStatus } from "Repos/Common/Contracts/Policy";
import * as Resources from "Repos/Views/PullRequest/Resources";
import { GlobalMessageLevel, IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import { WebApiTagDefinition } from "Tfs/Platform/Contracts/Core";
import {
    IReadonlyObservableArray,
    IReadonlyObservableValue,
    IReadyableReadonlyObservableArray,
    ObservableArray,
    ObservableValue,
    ReadyableObservableArray
} from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IPageStateHistoryService } from "VSS/Features/History";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { IVssContributionEventService, IVssContributionService } from "VSS/Platform/Contribution";
import { IPullRequestPolicyEvaluation, PullRequestIterationChanges } from "./Contracts/PullRequest";
import { getGitClient } from "./RestClient/Git";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

/**
 * Types of PR data to fetch when getting details
 */
export const enum PullRequestDataTypes {
    basicDetails = 1,
    autoComplete = 2,
    changes = 4,
    discussionThreads = 8,
    iterations = 16,
    labels = 32,
    policies = 64,
    statuses = 128,
    latestChanges = 256,
    threadContextsForLatestChanges = 512,
    reinvalidatePolicyCache = 1024,

    /**
     * Default refresh. Excluding changes (only refreshed when iteration changes)
     */
    default = basicDetails |
        autoComplete |
        discussionThreads |
        iterations |
        labels |
        policies |
        statuses |
        latestChanges |
        threadContextsForLatestChanges
}

export type ThreadContextsForLatestChangesMap = { [id: number]: CommentThreadContext };

//

/** Using Partial<> this way lets you specify just the PullRequest fields you want to update (which
 * the API demands anyway), while preserving full type safety.
 * */
export type PullRequestPartialUpdate = Partial<
    Omit<GitPullRequest, "autoCompleteSetBy" | "completionOptions"> & {
        autoCompleteSetBy: Partial<IdentityRef>;
        completionOptions: Partial<GitPullRequestCompletionOptions>;
    }
>;

/**
 * Service used for seeding data to the PR view and policy services.
 *
 * Should not be used directly by any components
 */
export interface IInternalPullRequestDataService extends IVssService {
    /**
     * Gets the current auto-complete data for the current pull request
     */
    getAutoCompleteData(): IReadonlyObservableValue<IPullRequestAutoCompleteData | undefined>;

    /**
     * Gets the file changes associated with the selected iteration
     */
    getChanges(): IReadonlyObservableValue<PullRequestIterationChanges | undefined>;

    /**
     * Gets the current list of discussion threads for the current pull request
     */
    getDiscussionThreads(): IReadonlyObservableArray<GitPullRequestCommentThread>;

    /**
     * Gets the set of iterations for this pull request
     */
    getIterations(): IReadonlyObservableArray<GitPullRequestIteration>;

    /**
     * On demand fetches changes for any iteration. To be used only when it's not either selected nor latest iteration.
     */
    getIterationChanges(iterationId: number, baseIterationId: number): Promise<PullRequestIterationChanges | undefined>;

    /**
     * Gets the set of labels (tags) applied to this pull request
     */
    getLabels(): IReadonlyObservableArray<WebApiTagDefinition>;

    /**
     * Gets the file changes associated with the latest iteration
     */
    getLatestChanges(): IReadonlyObservableValue<PullRequestIterationChanges | undefined>;

    /**
     * Gets the list of discussion threads relative to the latest changes (in terms of code locations)
     */
    getThreadContextsForLatestChanges(): ThreadContextsForLatestChangesMap;

    /**
     * Gets the current set of pull request policies
     */
    getPolicies(): IReadyableReadonlyObservableArray<IPullRequestPolicyEvaluation>;

    /**
     * Gets basic details about the current pull request
     */
    getPullRequestBasicData(): IReadonlyObservableValue<IPullRequestBasicData>;

    /**
     * Gets the set of statuses associated with this pull request
     */
    getStatuses(): IReadyableReadonlyObservableArray<GitPullRequestStatus>;

    /**
     * Update the pull request with the server, and the observable value in the data service.
     * @param changes - A partial PR record containing just the fields you want to update.
     * @param refreshDataTypes (optional) - data types which should be automatically updated. This happens after the returned
     * promise resolves.
     */
    gitClientUpdatePullRequest(changes: PullRequestPartialUpdate, refreshDataTypes?: PullRequestDataTypes): Promise<void>;

    /**
     * Double the maximum amount of changed files to display and re-fetch the PR's changes.
     */
    loadMoreChanges(): void;

    /**
     * Updates DataService's view of the pull request in response to gitClient methods returning a current
     * version of the pull request record.
     */
    pullRequestUpdated(updatedPR: GitPullRequest): void;
    
     /**
     * Indicates whether pr has multiple merge bases or not 
     */
    getMergeBasesStatus(): boolean;

    /**
     * Refresh pull request data.
     *
     * @param types Types of data to refresh
     */
    refreshPullRequestData(types: PullRequestDataTypes): Promise<void>;

    /**
     * Sets the current iteration to be used when updating discussions
     
     * @param baseIterationId Base iteration id
     * @param iterationId Current iteration id
     */
    setIteration(iterationId: number, baseIterationId: number): void;

    /**
     * Indicates whether pr's data uses _cached_ policy evaluation results
     */
    getIsPolicyResultsCached(): boolean;
}

export interface ISourceRefStatus {
    hasPolicies?: boolean;
    isDeleted?: boolean;
    isDefault?: boolean;
    isFastForwardable?: boolean;
    maxRebaseCommitCount?: number;
}

/**
 * Basic details of a pull request
 */
export interface IPullRequestBasicData {
    latestIterationId: number;
    preferences: IPullRequestPreferencesData;
    pullRequest: GitPullRequest;
    sourceRefs?: GitRef[];
    sourceRefStatus?: ISourceRefStatus;
    targetRefs: GitRef[];
}

const pullRequestDetailsDataProviderId = "ms.vss-code-web.pr-detail-data-provider";
interface IPullRequestDetailsData {
    autoCompleteData?: IPullRequestAutoCompleteData;
    changes?: PullRequestIterationChanges;
    discussionThreads?: GitPullRequestCommentThread[];
    iterations?: GitPullRequestIteration[];
    labels?: WebApiTagDefinition[];
    latestChanges?: PullRequestIterationChanges;
    threadContextsForLatestChanges?: PullRequestDiscussionThreadContext[];
    latestIterationId: number;
    preferences?: IPullRequestPreferencesData;
    policies?: IPullRequestPolicyEvaluation[];
    pullRequest?: GitPullRequest;
    sourceRefs?: GitRef[];
    sourceRefStatus?: ISourceRefStatus;
    statuses?: GitPullRequestStatus[];
    targetRefs?: GitRef[];
    policyCacheMetadata?: IPolicyEvaluationCacheMetadata;
}

interface PullRequestDiscussionThreadContext {
    id: number;
    threadContext: CommentThreadContext;
}

export interface IPullRequestAutoCompleteData {
    blockingPolicyIds: number[];
}

export interface IPullRequestPreferencesData {
    witTransitionsSticky: boolean;
}

export interface IPolicyEvaluationCacheMetadata {
    pullRequestChangesCount: number;
}

class InternalPullRequestDataService extends VssService implements IInternalPullRequestDataService {
    private autoCompleteData: ObservableValue<IPullRequestAutoCompleteData | undefined>;
    private changes: ObservableValue<PullRequestIterationChanges | undefined>;
    private latestChanges: ObservableValue<PullRequestIterationChanges | undefined>;
    private discussionThreads: ObservableArray<GitPullRequestCommentThread>;
    private iterations: ObservableArray<GitPullRequestIteration>;
    private threadContextsForLatestChanges: ThreadContextsForLatestChangesMap;
    private policies: ReadyableObservableArray<IPullRequestPolicyEvaluation>;
    private pullRequestBasicData: ObservableValue<IPullRequestBasicData>;
    private labels: ObservableArray<WebApiTagDefinition>;
    private statuses: ReadyableObservableArray<GitPullRequestStatus>;

    private pullRequestUpdates: { [dataType: number]: number } = {};
    private pullRequestUpdateIndex = 0;

    private latestIterationId = 0;
    private baseIterationId = 0;
    private selectedIterationId = 0;
    private hasMultipleMergeBases = false;
    private isPolicyResultsCached = false;

    private findImageRegexp = /!\[(.*)\]\((https:\/\/.*)\)/;

    private devAzureDomainRegexp = /https:\/\/dev.azure.com\/([^\/\?]+)\/(.*)/;
    private visualStudioDomainRegexp = /https:\/\/(.*).visualstudio.com\/(.*)/;

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
        const data = contributionService.getData<IPullRequestDetailsData>(pullRequestDetailsDataProviderId);
        if (!data) {
            throw new Error("Failed to load PR contribution data");
        }

        this.pullRequestBasicData = new ObservableValue({
            latestIterationId: data.latestIterationId,
            preferences: data.preferences!,
            pullRequest: data.pullRequest!,
            sourceRefs: data.sourceRefs!,
            sourceRefStatus: data.sourceRefStatus,
            targetRefs: data.targetRefs!
        });

        this.labels = new ObservableArray(data.labels);

        this.iterations = new ObservableArray(data.iterations);
        this.initializeIterations();
        this.latestChanges = new ObservableValue(
            data.latestChanges ? { ...data.latestChanges, baseIterationId: 0, iterationId: this.latestIterationId } : undefined
        );

        if (data.latestChanges && !data.changes) {
            data.changes = data.latestChanges;
        }

        this.changes = new ObservableValue(
            data.changes
                ? {
                      ...data.changes,
                      baseIterationId: this.baseIterationId,
                      iterationId: this.selectedIterationId
                  }
                : undefined
        );

        this.autoCompleteData = new ObservableValue(data.autoCompleteData);

        if (data.discussionThreads) {
            data.discussionThreads.forEach(t => t.comments?.forEach(c => 
                    c.content = this.replaceDiffDomainUrls(c.content, document.URL)));
        }

        this.discussionThreads = new ObservableArray(data.discussionThreads);
        this.threadContextsForLatestChanges = processThreadContextsForLatestChanges(data);

        this.policies = new ReadyableObservableArray<IPullRequestPolicyEvaluation>();
        this.statuses = new ReadyableObservableArray();
        this.hasMultipleMergeBases = data.pullRequest?.hasMultipleMergeBases ?? false;
        this.isPolicyResultsCached = data.policyCacheMetadata != null;

        // Kick off fetch of policies and statuses information asynchronously
        this.refreshPullRequestData(PullRequestDataTypes.policies | PullRequestDataTypes.statuses);
    }

    public getAutoCompleteData(): IReadonlyObservableValue<IPullRequestAutoCompleteData | undefined> {
        return this.autoCompleteData;
    }

    public getChanges(): IReadonlyObservableValue<PullRequestIterationChanges | undefined> {
        return this.changes;
    }

    public getMergeBasesStatus(): boolean {
        return this.hasMultipleMergeBases;
    }

    public getDiscussionThreads(): IReadonlyObservableArray<GitPullRequestCommentThread> {
        return this.discussionThreads;
    }

    public getIterations(): IReadonlyObservableArray<GitPullRequestIteration> {
        return this.iterations;
    }

    public getIterationChanges(iterationId: number, baseIterationId: number): Promise<PullRequestIterationChanges | undefined> {
        return this.getPullRequestData(PullRequestDataTypes.changes, undefined, undefined, iterationId, baseIterationId).then(data => data?.changes);
    }

    public getLabels(): IReadonlyObservableArray<WebApiTagDefinition> {
        return this.labels;
    }

    public getLatestChanges(): IReadonlyObservableValue<PullRequestIterationChanges | undefined> {
        return this.latestChanges;
    }

    public getThreadContextsForLatestChanges(): ThreadContextsForLatestChangesMap {
        return this.threadContextsForLatestChanges;
    }

    public getPolicies(): IReadyableReadonlyObservableArray<IPullRequestPolicyEvaluation> {
        return this.policies;
    }

    public getPullRequestBasicData(): IReadonlyObservableValue<IPullRequestBasicData> {
        return this.pullRequestBasicData;
    }

    public getStatuses(): IReadyableReadonlyObservableArray<GitPullRequestStatus> {
        return this.statuses;
    }

    public getIsPolicyResultsCached(): boolean {
        return this.isPolicyResultsCached;
    }

    public async loadMoreChanges(): Promise<void> {
        if (this.changes.value) {
            const data = await this.getPullRequestData(PullRequestDataTypes.changes, this.changes.value.nextSkip, this.changes.value.nextTop);
            if (data && data.changes) {
                this.changes.value = {
                    ...data.changes,
                    changeEntries: [...this.changes.value.changeEntries, ...data.changes.changeEntries],
                    baseIterationId: this.baseIterationId,
                    iterationId: this.selectedIterationId,
                    nextTop: data.changes.nextTop * 2
                };

                if (this.baseIterationId === 0 && this.selectedIterationId === this.latestIterationId) {
                    this.latestChanges.value = this.changes.value;
                }
            }
        }
    }

    private replaceDiffDomainUrls(content: string, referrerUrl: string) : string {
        if (!content)
            return content;

        if (!referrerUrl)
            return content;

        const referrerDetails = this.parseUrl(referrerUrl);
        if (!referrerDetails)
            return content;
        
        const alias = referrerDetails[1];

        let newContent = content || "";
        let blobMatch = content.match(this.findImageRegexp);

        const { hostname } = new URL(referrerUrl);
        const isVisualStudioDomain = hostname.includes("visualstudio.com");

        while (blobMatch) {
            content = content.replace(blobMatch[0], "");
            let url = blobMatch[2];

            var details = this.parseUrl(url);
            if (details && alias == details[1] && !details[0].includes(hostname)) {
                const newUrl = "https://" + (isVisualStudioDomain ? 
                    `${alias}.visualstudio.com/${details[2]}` :
                    `dev.azure.com/${alias}/${details[2]}`);

                newContent = newContent.replace(details[0], newUrl);
            }

            blobMatch = content.match(this.findImageRegexp);
        }

        return newContent;
    }

    /**
     * Parse URL to get alias and path in an array.
     *
     * @param url referrer url
     */
    private parseUrl(url: string): RegExpMatchArray | null {
        return url.match(this.devAzureDomainRegexp) || url.match(this.visualStudioDomainRegexp);
    }

    public refreshPullRequestData(types: PullRequestDataTypes): Promise<void> {
        const pullRequestUpdateIndex = this.pullRequestUpdateIndex++;
        let fireEvent: boolean = false;

        return this.getPullRequestData(types).then(data => {
            if (data) {
                const previousPullRequestData = {
                    ...this.pullRequestBasicData.value.pullRequest
                };

                const pullRequest = data.pullRequest!;
                if (pullRequest) {
                    pullRequest.description = this.replaceDiffDomainUrls(pullRequest.description, document.URL);
                }

                this.updatePullRequestData(types, PullRequestDataTypes.basicDetails, pullRequestUpdateIndex, () => {
                    this.pullRequestBasicData.value = {
                        latestIterationId: data.latestIterationId,
                        preferences: data.preferences!,
                        pullRequest: pullRequest,
                        sourceRefs: data.sourceRefs!,
                        sourceRefStatus: data.sourceRefStatus,
                        targetRefs: data.targetRefs!
                    };
                    fireEvent = true;
                });

                this.updatePullRequestData(types, PullRequestDataTypes.autoComplete, pullRequestUpdateIndex, () => {
                    this.autoCompleteData.value = data.autoCompleteData;
                    this.isPolicyResultsCached = data.policyCacheMetadata != null;
                });

                this.updatePullRequestData(types, PullRequestDataTypes.discussionThreads, pullRequestUpdateIndex, () => {
                    if (data.discussionThreads) {
                        data.discussionThreads.forEach(t => t.comments?.forEach(c => 
                            c.content = this.replaceDiffDomainUrls(c.content, document.URL)));
                    }
                    this.discussionThreads.value = data.discussionThreads || [];
                });

                this.updatePullRequestData(types, PullRequestDataTypes.threadContextsForLatestChanges, pullRequestUpdateIndex, () => {
                    this.threadContextsForLatestChanges = processThreadContextsForLatestChanges(data);
                });

                this.updatePullRequestData(types, PullRequestDataTypes.iterations, pullRequestUpdateIndex, () => {
                    this.iterations.value = data.iterations || [];
                });

                this.updatePullRequestData(types, PullRequestDataTypes.labels, pullRequestUpdateIndex, () => {
                    this.labels.value = data.labels || [];
                });

                this.updatePullRequestData(types, PullRequestDataTypes.policies, pullRequestUpdateIndex, () => {
                    // Avoid non-applicable
                    this.policies.value = (data.policies || []).filter(p => p.status !== PolicyEvaluationStatus.NotApplicable);
                    if (!this.policies.ready.value) {
                        this.policies.ready.value = true;
                    }
                    this.isPolicyResultsCached = data.policyCacheMetadata != null;
                });

                this.updatePullRequestData(types, PullRequestDataTypes.statuses, pullRequestUpdateIndex, () => {
                    this.statuses.value = data.statuses || [];
                    if (!this.statuses.ready.value) {
                        this.statuses.ready.value = true;
                    }
                });

                // Don't process changes until after we've notified subscribers of new iterations, etc.
                this.updatePullRequestData(types, PullRequestDataTypes.latestChanges, pullRequestUpdateIndex, () => {
                    this.latestChanges.value = data.latestChanges
                        ? {
                              ...data.latestChanges,
                              baseIterationId: 0,
                              iterationId: data.latestIterationId
                          }
                        : undefined;
                });

                this.updatePullRequestData(types, PullRequestDataTypes.changes, pullRequestUpdateIndex, () => {
                    this.changes.value = data.changes
                        ? {
                              ...data.changes,
                              baseIterationId: this.baseIterationId,
                              iterationId: this.selectedIterationId
                          }
                        : undefined;
                });

                if (fireEvent) {
                    this.publishPullRequestDetailsUpdatedEvent(previousPullRequestData);
                }
            }
        });
    }

    public async gitClientUpdatePullRequest(changes: PullRequestPartialUpdate, refreshDataTypes?: PullRequestDataTypes): Promise<void> {
        let pullRequest: GitPullRequest = this.pullRequestBasicData.value.pullRequest;

        try {
            pullRequest = await getGitClient(this.pageContext).updatePullRequest(
                changes as GitPullRequest,
                pullRequest.repository.id,
                pullRequest.pullRequestId,
                pullRequest.repository.project.id
            );
        } catch (ex) {
            const globalMessageService = this.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");

            const status = ex.status | 0;
            const message = format(Resources.OperationFailed, `${ex.message}${status ? " (" + status + ")" : ""}`);

            globalMessageService.addBanner({
                message,
                dismissable: true,
                level: GlobalMessageLevel.error
            });

            return;
        }

        this.pullRequestUpdated(pullRequest);

        // Note: the next call is async but we don't await it. As a result, the call to refreshPullRequestData()
        // is effectively "fire and forget".

        if (refreshDataTypes !== undefined) {
            this.refreshPullRequestData(refreshDataTypes);
        }
    }

    /* Most of the git client operations in PullRequestViewService return an updated PR record. This keeps the pullRequest
     * in sync between PRView and Data services
     */
    public pullRequestUpdated(updatedPR: GitPullRequest) {
        const previousPullRequestData = {
            ...this.pullRequestBasicData.value.pullRequest
        };

        this.pullRequestBasicData.value = {
            ...this.pullRequestBasicData.value,
            pullRequest: updatedPR
        };

        this.publishPullRequestDetailsUpdatedEvent(previousPullRequestData);
        // Note that we don't increment the update counter pullRequestUpdates[PullRequestDataTypes.basicDetails].
        // This type of update is synchronous -- we already have the result when we call pullRequestUpdated().
        // Updating the counter would potentially cause other updates which are in-flight to be ignored when
        // they finish.
    }

    public setIteration(iterationId: number, baseIterationId: number): void {
        this.selectedIterationId = iterationId;
        this.baseIterationId = baseIterationId;
    }

    private updatePullRequestData(dataTypes: PullRequestDataTypes, type: PullRequestDataTypes, pullRequestUpdateIndex: number, update: () => void) {
        if ((dataTypes & type) !== 0) {
            if (!(this.pullRequestUpdates[type] > pullRequestUpdateIndex)) {
                update();
                this.pullRequestUpdates[type] = pullRequestUpdateIndex;
            }
        }
    }

    private publishPullRequestDetailsUpdatedEvent(previousPullRequest: GitPullRequest) {
        const contributedEventService = this.pageContext.getService<IVssContributionEventService>("IVssContributionEventService");
        const eventData = {
            pullRequest: this.pullRequestBasicData.value.pullRequest,
            previousPullRequest: previousPullRequest
        };
        contributedEventService.fireEvent("pull-request-details-updated", eventData, false);
    }

    private getPullRequestData(
        types: PullRequestDataTypes,
        skipChanges?: number,
        maxChanges?: number,
        iterationId = this.selectedIterationId,
        baseIterationId = this.baseIterationId
    ): Promise<IPullRequestDetailsData | undefined> {
        const pullRequest = this.pullRequestBasicData.value.pullRequest;
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService.getDataAsync<IPullRequestDetailsData>(
            pullRequestDetailsDataProviderId,
            {
                baseIterationId,
                iterationId,
                maxChanges,
                pullRequestId: pullRequest.pullRequestId,
                repositoryId: pullRequest.repository.id,
                skipChanges,
                types
            },
            true
        );
    }

    private initializeIterations() {
        const pageStateHistoryService = this.pageContext.getService<IPageStateHistoryService>("IPageStateHistoryService");
        const historyState = pageStateHistoryService.getProjectedState();

        // Get latest iteration id
        this.iterations.value.forEach(it => (this.latestIterationId = Math.max(it.id, this.latestIterationId)));

        this.baseIterationId = parseInt(historyState["base"]) || 0;
        this.selectedIterationId = parseInt(historyState["iteration"]) || this.latestIterationId;
    }
}

function processThreadContextsForLatestChanges(data: IPullRequestDetailsData): ThreadContextsForLatestChangesMap {
    const map: ThreadContextsForLatestChangesMap = {};
    for (const thread of data.threadContextsForLatestChanges || data.discussionThreads || []) {
        if (thread.threadContext) {
            map[thread.id] = thread.threadContext;
        }
    }

    return map;
}

export function getPullRequestDataService(pageContext: IVssPageContext): IInternalPullRequestDataService {
    return pageContext.getService<IInternalPullRequestDataService>("IInternalPullRequestDataService");
}

Services.add("IInternalPullRequestDataService", { serviceFactory: InternalPullRequestDataService });
